import { useMemo, useState } from "react";
import { useNavigate } from "react-router";

import type { SurveyDto } from "@/api/types";
import { AskNobiButton } from "@/components/AskNobi/AskNobiButton";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ContextMenu, type ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { DeleteModal, useDeleteModal } from "@/components/DeleteModal/DeleteModal";
import { FormattedDate } from "@/components/FormattedDate/FormattedDate";
import { Download01Icon } from "@/components/Icons/Download01Icon";
import { Edit05Icon } from "@/components/Icons/Edit05Icon";
import { Trash02Icon } from "@/components/Icons/Trash02Icon";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Pagination } from "@/components/Pagination/Pagination";
import { SearchInput } from "@/components/SearchInput/SearchInput";
import { useShareEntityMenuItems } from "@/components/ShareButton/helpers";
import { ShareToChatModal } from "@/components/ShareToChatModal/ShareToChatModal";
import { ShareToCommunityFeedModal } from "@/components/ShareToCommunityFeedModal/ShareToCommunityFeedModal";
import { User, UserTooltipContent } from "@/components/User/User";
import { Button } from "@/design-system/Button";
import { Card } from "@/design-system/Card";
import { createDashboardColumnHelper } from "@/design-system/Table/helpers";
import { TABLE_COLUMN_SIZES } from "@/design-system/Table/helpers";
import { Table } from "@/design-system/Table/Table";
import { Text } from "@/design-system/Text";
import { getIsSurveySharable } from "@/helpers/surveys";
import { isDefined } from "@/helpers/util";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { usePermission } from "@/hooks/usePermission";
import { TABLE_KEYS, useResizableTable } from "@/hooks/useResizableTable";
import { useSlug } from "@/hooks/useSlug";
import { SurveyExportModal } from "@/modules/surveys/pages/details/components/SurveyExportModal";
import { canManageSurvey } from "@/modules/surveys/permissions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export interface SurveysListProps {
  isLoading: boolean;
  surveys: SurveyDto[];
  page: number;
  totalPages: number;
  totalSurveys?: number;
  searchQuery: string;
  debouncedQuery: string;
  onPageChange: (page: number) => void;
  onSearch: (value: string) => void;
  onDelete: (surveyId: string) => Promise<unknown>;
}

export function SurveysList({
  surveys,
  isLoading,
  totalPages,
  totalSurveys,
  page,
  searchQuery,
  debouncedQuery,
  onPageChange,
  onDelete,
  onSearch,
}: SurveysListProps): React.ReactNode {
  const [rowIdWithActionMenuOpened, setRowIdWithActionMenuOpened] = useState<string | undefined>(undefined);
  const [isShareToCommunityFeedModalOpened, shareToCommunityFeedModalHandler] = useBool(false);
  const [isShareToChatModalOpened, shareToChatModalHandler] = useBool(false);
  const [surveyExportModalId, setSurveyExportModalId] = useState<string | undefined>();
  const [isExportModalOpened, exportModalHandler] = useBool(false);

  const sessionUser = useSessionUser();
  const slug = useSlug();
  const projectId = useProjectId();
  const { t } = useTranslation();
  const hasPermission = usePermission();
  const { componentProps: deleteModalProps, openDeleteModal } = useDeleteModal<SurveyDto>("survey-delete-modal");
  const columns = useMemo(() => {
    const helper = createDashboardColumnHelper<SurveyDto>();

    return [
      helper.accessor("title", {
        header: t("page.surveys.table.title"),
        cell: (cell) => cell.getValue(),
        meta: {
          shouldSimulateRowClick: true,
          renderTruncatedTooltip: (cell) => cell.getValue(),
        },
        minSize: TABLE_COLUMN_SIZES.LEADING_CONTENT.MIN,
        size: TABLE_COLUMN_SIZES.LEADING_CONTENT.DEFAULT,
      }),
      helper.accessor("createdBy", {
        header: t("page.surveys.table.user"),
        cell: (cell) => {
          const data = cell.getValue();

          if (!data) {
            return <span>{t("common.label.not-applicable")}</span>;
          }

          return <User user={data} size="sm" shouldNavigate={false} />;
        },
        minSize: TABLE_COLUMN_SIZES.USER.MIN,
        size: TABLE_COLUMN_SIZES.USER.DEFAULT,
        meta: {
          renderTruncatedTooltip: (cell) => {
            const data = cell.getValue();

            if (!data) {
              return null;
            }

            return <UserTooltipContent fullName={data.fullName} />;
          },
        },
      }),
      helper.accessor("group.name", {
        header: t("page.surveys.table.group"),
        cell: (cell) => <span>{cell.getValue() || t("page.surveys.table.group.general")}</span>,
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
      helper.accessor("startsAt", {
        header: t("page.surveys.table.start"),
        cell: (cell) => {
          const date = cell.getValue();

          return (
            <span className="whitespace-nowrap">
              {date ? <FormattedDate date={date} format="dateMonthYearShort" /> : <span>-</span>}
            </span>
          );
        },
        minSize: TABLE_COLUMN_SIZES.DATE.MIN,
        size: TABLE_COLUMN_SIZES.DATE.DEFAULT,
        meta: {
          renderTruncatedTooltip: (cell) => {
            const date = cell.getValue();

            return date ? <FormattedDate date={date} format="dateMonthYearShort" /> : null;
          },
        },
      }),
      helper.accessor("endsAt", {
        header: t("page.surveys.table.end"),
        cell: (cell) => {
          const date = cell.getValue();

          return (
            <span className="whitespace-nowrap">
              {date ? <FormattedDate date={date} format="dateMonthYearShort" /> : <span>-</span>}
            </span>
          );
        },
        meta: {
          renderTruncatedTooltip: (cell) => {
            const date = cell.getValue();

            return date ? <FormattedDate date={date} format="dateMonthYearShort" /> : null;
          },
        },
        minSize: TABLE_COLUMN_SIZES.DATE.MIN,
        size: TABLE_COLUMN_SIZES.DATE.DEFAULT,
      }),
      helper.accessor("questionCount", {
        header: t("page.surveys.table.questions"),
        cell: (cell) => <span>{cell.getValue()}</span>,
        minSize: TABLE_COLUMN_SIZES.COUNT.MIN,
        size: TABLE_COLUMN_SIZES.COUNT.DEFAULT,
      }),
      helper.accessor("answerCount", {
        header: t("page.surveys.table.responses"),
        cell: (cell) => <span>{cell.getValue()}</span>,
        minSize: TABLE_COLUMN_SIZES.COUNT.MIN,
        size: TABLE_COLUMN_SIZES.COUNT.DEFAULT,
      }),
      helper.accessor("id", {
        header: "",
        cell: (cell) => {
          const survey = cell.row.original;

          return (
            <SurveyTableRowActions
              survey={survey}
              onExport={() => {
                setSurveyExportModalId(survey.id);
                exportModalHandler.setTrue();
              }}
              onDelete={() => openDeleteModal(survey)}
              onShareToCommunityFeed={() => {
                setRowIdWithActionMenuOpened(survey.id);
                shareToCommunityFeedModalHandler.setTrue();
              }}
              onShareToChat={() => {
                setRowIdWithActionMenuOpened(survey.id);
                shareToChatModalHandler.setTrue();
              }}
            />
          );
        },
        size: TABLE_COLUMN_SIZES.ACTION.DEFAULT,
        enableResizing: false,
        meta: {
          isAction: true,
        },
      }),
    ];
  }, [t, exportModalHandler, openDeleteModal, shareToCommunityFeedModalHandler, shareToChatModalHandler]);

  const tableInstance = useResizableTable(TABLE_KEYS.SURVEYS, {
    columns,
    data: surveys,
    enableSorting: false,
  });
  const surveyToExport = surveyExportModalId ? surveys.find((x) => x.id === surveyExportModalId) : undefined;

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={t("page.surveys.list.title")}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: sessionUser.project.name,
            },
            {
              name: t("page.surveys.list.title"),
            },
          ]}
        />
      }
      header={
        <SearchInput
          width="standard"
          value={searchQuery}
          onChange={(e) => onSearch(e.target.value)}
          placeholder={t("page.surveys.list.search.placeholder")}
        />
      }
      actions={
        <>
          <AskNobiButton
            defaultFilters={{ projectIds: [projectId] }}
            context={{
              scope: "project",
              entity: "surveys",
              page: "list",
              filters: {
                search: debouncedQuery,
              },
            }}
            suggestions={[
              t("component.ask-nobi-chat-drawer.surveys.suggestion.1"),
              t("component.ask-nobi-chat-drawer.surveys.suggestion.2"),
            ]}
          />
          {hasPermission(canManageSurvey) && (
            <Button type="link" data-testid="create-new-survey-btn" href={routes.surveys.new({ slug })}>
              {t("page.surveys.list.create")}
            </Button>
          )}
        </>
      }
    >
      {isDefined(totalSurveys) && (
        <p className="pb-space-sm text-caption text-text-secondary">
          {t("page.surveys.list.survey-count", { count: totalSurveys })}
        </p>
      )}
      {(surveys.length > 0 || isLoading) && (
        <Table
          table={tableInstance}
          isLoading={isLoading}
          getRowHref={(row) => routes.surveys.details({ slug, id: row.original.id })}
        >
          <Pagination count={totalPages} onChange={onPageChange} currentIndex={page} />
        </Table>
      )}
      {!isLoading && surveys.length === 0 && (
        <Card>
          <Text as="p">{debouncedQuery ? t("page.surveys.list.no-result") : t("page.surveys.list.empty")}</Text>
        </Card>
      )}
      <DeleteModal
        title={t("page.surveys.actions.delete.title")}
        description={t("page.surveys.actions.delete.description")}
        onDelete={(survey) => onDelete(survey.id)}
        deleteBtnProps={{
          "data-testid": "survey-action-delete",
        }}
        {...deleteModalProps}
      />
      {surveyToExport && (
        <SurveyExportModal
          isOpened={isExportModalOpened && Boolean(surveyToExport)}
          onOpenChange={exportModalHandler.set}
          onExitComplete={() => setSurveyExportModalId(undefined)}
          survey={surveyToExport}
        />
      )}
      <ShareToCommunityFeedModal
        isOpened={isShareToCommunityFeedModalOpened}
        onOpenChange={shareToCommunityFeedModalHandler.set}
        entityId={rowIdWithActionMenuOpened || ""}
        entityType="survey"
      />
      <ShareToChatModal
        isOpened={isShareToChatModalOpened}
        onOpenChange={shareToChatModalHandler.set}
        entityType="survey"
        entityId={rowIdWithActionMenuOpened || ""}
      />
    </DocumentPage>
  );
}

interface SurveyTableRowActionsProps {
  survey: SurveyDto;
  onExport: () => void;
  onDelete: () => void;
  onShareToCommunityFeed: () => void;
  onShareToChat: () => void;
}

function SurveyTableRowActions({
  survey,
  onExport,
  onDelete,
  onShareToCommunityFeed,
  onShareToChat,
}: SurveyTableRowActionsProps): React.ReactNode {
  const { t } = useTranslation();
  const slug = useSlug();
  const navigate = useNavigate();

  const shareEntityMenuItems = useShareEntityMenuItems({
    deepLinkPayload: {
      entityType: "survey",
      entityId: survey.id,
    },
    callbacks: {
      shareToCommunityFeed: onShareToCommunityFeed,
      shareToChat: onShareToChat,
    },
  });

  const menuActions: ContextMenuItem[] = [
    {
      labels: { default: t("common.action.edit") },
      value: "edit",
      icon: <Edit05Icon />,
      callbackFn: () => navigate(routes.surveys.edit({ slug, id: survey.id })),
      isHidden: !survey.canEdit,
      "data-testid": "context-menu-edit-btn",
    },
    {
      labels: { default: t("page.surveys.table.context.export") },
      value: "export",
      icon: <Download01Icon />,
      callbackFn: onExport,
      isHidden: !survey.answerCount,
      "data-testid": "context-menu-export-btn",
    },
    {
      labels: { default: t("common.action.delete") },
      value: "delete",
      icon: <Trash02Icon />,
      callbackFn: onDelete,
      isHidden: !survey.canDelete,
      "data-testid": "context-menu-delete-btn",
    },
    ...(getIsSurveySharable(survey) ? shareEntityMenuItems : []),
  ];

  return <ContextMenu items={menuActions} />;
}
